import { useCallback, useEffect, useState } from "react";
import {
  Check,
  History,
  Loader2,
  RotateCcw,
  Undo2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi, messageFrom } from "@/lib/api";
import { formatDateTime, money, relativeTime } from "@/lib/format";

const ACTION_LABELS = {
  baseline: "Starting point",
  save: "Edited",
  reset: "Reset to defaults",
  restore: "Restored",
};

const ACTION_STYLES = {
  baseline: "border-border bg-muted text-muted-foreground",
  save: "border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] text-[color:var(--tt-teal-deep)]",
  reset: "border-border bg-muted text-muted-foreground",
  restore:
    "border-[color:var(--tt-warn-border)] bg-[color:var(--tt-warn-bg)] text-[color:var(--tt-warn-text)]",
};

const formatValue = (value, kind) => {
  if (value === null || value === undefined) return "";
  return kind === "percent" ? `${value}%` : money(value);
};

const ChangeRow = ({ change }) => (
  <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-[13px]">
    <span className="text-muted-foreground">
      <span className="font-semibold text-foreground">{change.service_label}</span>
      {" \u00b7 "}
      {change.label}
    </span>
    {change.type === "table" ? (
      <span className="font-amount text-xs font-semibold text-[color:var(--tt-warn-text)]">
        {change.summary}
      </span>
    ) : (
      <span className="font-amount whitespace-nowrap text-xs font-semibold">
        <span className="text-muted-foreground line-through">
          {formatValue(change.before, change.type)}
        </span>
        {" \u2192 "}
        <span className="text-primary">{formatValue(change.after, change.type)}</span>
      </span>
    )}
  </li>
);

/** One entry in the history: what changed, who changed it, and when. */
const HistoryItem = ({ item, onRestore }) => (
  <li
    data-testid="rate-card-history-item"
    className={`rounded-lg border p-3.5 ${
      item.is_current
        ? "border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)]"
        : "border-border bg-[color:var(--tt-surface-2)]"
    }`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className={`px-1.5 py-0 text-[10px] font-semibold uppercase tracking-[0.06em] ${
              ACTION_STYLES[item.action] || ACTION_STYLES.save
            }`}
          >
            {ACTION_LABELS[item.action] || item.action}
          </Badge>
          {item.is_current ? (
            <Badge
              variant="outline"
              data-testid="rate-card-history-current"
              className="gap-1 border-[color:var(--tt-teal-line)] bg-white px-1.5 py-0 text-[10px] font-semibold text-[color:var(--tt-teal-deep)]"
            >
              <Check className="h-2.5 w-2.5" aria-hidden="true" />
              Live now
            </Badge>
          ) : null}
          {item.is_default ? (
            <Badge
              variant="outline"
              className="border-border bg-white px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
            >
              Shipped defaults
            </Badge>
          ) : null}
        </div>
        <p className="mt-2 text-[13px] font-medium leading-relaxed text-foreground">
          {item.summary}
        </p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          <UserRound className="h-3 w-3" aria-hidden="true" />
          <span>{item.author}</span>
          <span aria-hidden="true">·</span>
          <span title={formatDateTime(item.created_at)}>{relativeTime(item.created_at)}</span>
          <span aria-hidden="true">·</span>
          <span>{formatDateTime(item.created_at)}</span>
        </p>
      </div>

      {item.is_current ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onRestore(item)}
          data-testid={`rate-card-restore-${item.version_id}`}
          className="h-9 shrink-0 bg-white"
        >
          <Undo2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Restore
        </Button>
      )}
    </div>

    {item.changes?.length ? (
      <ul className="mt-3 space-y-1.5 border-t border-border pt-3">
        {item.changes.map((change) => (
          <ChangeRow key={`${change.service}-${change.path}`} change={change} />
        ))}
      </ul>
    ) : null}
  </li>
);

/** The list, or whatever is standing in for it while there is nothing to show. */
const HistoryBody = ({ loading, items, onRestore }) => {
  if (loading && !items.length) {
    return (
      <div className="space-y-3" data-testid="rate-card-history-loading">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">No changes recorded yet.</p>
    );
  }
  return (
    <ol className="space-y-3">
      {items.map((item) => (
        <HistoryItem key={item.version_id} item={item} onRestore={onRestore} />
      ))}
    </ol>
  );
};

export const RateCardHistory = ({ refreshKey, onRestored, onAuthError }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(12);
  const [pending, setPending] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(
    async (take) => {
      setLoading(true);
      try {
        const data = await adminApi.rateCardHistory({ limit: take });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (error) {
        if (!onAuthError?.(error)) toast.error(messageFrom(error));
      } finally {
        setLoading(false);
      }
    },
    [onAuthError],
  );

  useEffect(() => {
    load(limit);
  }, [load, limit, refreshKey]);

  const restore = async () => {
    if (!pending) return;
    setRestoring(true);
    try {
      const data = await adminApi.restoreRateCard(pending.version_id);
      toast.success("Rate card restored — new quotes use these figures immediately.");
      setPending(null);
      onRestored?.(data);
      load(limit);
    } catch (error) {
      if (!onAuthError?.(error)) toast.error(messageFrom(error));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <section
      data-testid="rate-card-history"
      className="mt-6 rounded-xl border border-border bg-white"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-[color:var(--tt-surface-2)] px-4 py-3">
        <div>
          <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
            <History className="h-4 w-4 text-primary" aria-hidden="true" />
            Change history
          </h4>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            Every pricing change, who made it and exactly what moved. Restore any earlier version
            in one click — quotes already issued keep the figures they were priced at.
          </p>
        </div>
        <Badge
          variant="outline"
          data-testid="rate-card-history-count"
          className="border-border font-normal text-muted-foreground"
        >
          {total} version{total === 1 ? "" : "s"}
        </Badge>
      </header>

      <div className="p-4">
        <HistoryBody loading={loading} items={items} onRestore={setPending} />

        {items.length < total ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setLimit((current) => current + 20)}
            disabled={loading}
            data-testid="rate-card-history-more"
            className="mt-4 w-full bg-white"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            Show older changes ({total - items.length} more)
          </Button>
        ) : null}
      </div>

      <AlertDialog open={Boolean(pending)} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version of the rate card?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  Pricing goes back to how it was{" "}
                  {pending ? relativeTime(pending.created_at) : ""} — the version saved by{" "}
                  <strong className="font-semibold text-foreground">{pending?.author}</strong>.
                  Quotes already issued keep the figures they were priced at; only new quotes are
                  affected.
                </p>
                <p className="mt-2">
                  This is recorded as a new entry in the history, so nothing is lost and you can
                  always come back.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rate-card-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                restore();
              }}
              disabled={restoring}
              data-testid="rate-card-restore-confirm"
            >
              {restoring ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Restore it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
